<template>
    <div class="settings-container">
        <div class="voice-row--header">
            <div class="header-cell">Voice</div>
            <div class="header-cell">Weight</div>
            <div class="header-cell"></div>
        </div>
        <div class="voices-container" ref="voicesList">
            <div v-for="(weight, voice) in localConfig.voice_weights" :key="voice" class="voice-row">
                <div class="voice-card">
                    <span class="voice-name-display">{{ formatKokoroVoiceName(voice) }}</span>
                </div>
                <div class="voice-weight">
                    <input type="number" :value="weight" @input="updateWeight(voice, $event.target.value)" min="0" />
                </div>
                <button class="btn-delete" @click="removeVoice(voice)" aria-label="Delete voice">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M3 6h18v2H3V6zm2 3h14l-1.1 12.1c-.1 1.1-1 1.9-2.1 1.9H8.1c-1.1 0-2-.8-2.1-1.9L5 9zm5-7h4v2h-4V2z" />
                    </svg>
                </button>
            </div>
        </div>

        <button class="btn-add-voice" @click="addVoice">Add Voice</button>

        <VoicePickerModal v-model:show="showVoicePicker" :items="allVoices" title="Select a Voice"
            placeholder="Search by voice name…" label-key="voice_name" sub-label-key="model"
            :search-keys="['voice_name', 'name', 'voice']" @select="selectVoiceFromPicker" />

        <div class="speed-container">
            <label class="section-label">Speed</label>
            <div class="slider-wrapper">
                <input type="range" v-model.number="localConfig.speed" min="0.3" max="4.0" step="0.1"
                    @change="emitUpdate" />
                <span class="slider-value">{{ (localConfig.speed ?? 0).toFixed(2) }}</span>
            </div>
        </div>
    </div>
</template>

<script>
import { getVoicesByUserId } from '@/api/services'
import { defineComponent, reactive, watch, nextTick, ref, computed } from 'vue';
import VoicePickerModal from '@/components/common/VoicePickerModal.vue';
import { formatKokoroVoiceName } from '@/utils/voiceDisplayName';
export default defineComponent({
    name: 'SettingsKokoroV1_0',
    components: { VoicePickerModal },
    props: {
        config: { type: Object, required: true }
    },
    emits: ['update:config'],
    setup(props, { emit }) {
        const voicesList = ref(null);
        const localConfig = reactive({
            voice_weights: props.config.voice_weights || {},
            speed: props.config.speed ?? 1.0
        });
        const allVoices = ref([]);

        const showVoicePicker = ref(false);
        const voiceSearchTerm = ref('');
        const filteredVoices = computed(() => {
            const q = voiceSearchTerm.value.trim().toLowerCase();
            if (!q) return allVoices.value;
            return allVoices.value.filter(v => {
                const name = (v.voice_name || v.name || v.voice || '').toLowerCase();
                return name.includes(q);
            });
        });

        loadVoices();

        function emitUpdate() {
            emit('update:config', { ...localConfig });
        }

        function updateWeight(voice, value) {
            localConfig.voice_weights[voice] = Number(value);
            emitUpdate();
        }

        function removeVoice(voice) {
            delete localConfig.voice_weights[voice];
            emitUpdate();
        }

        async function loadVoices() {
            try {
                const voicesDefault = await getVoicesByUserId();
                // const voicesUser = await getUserVoicesByUserId();

                // const combined = [
                //     ...(Array.isArray(voicesDefault) ? voicesDefault : []),
                //     ...(Array.isArray(voicesUser) ? voicesUser : [])
                // ];

                allVoices.value = voicesDefault.filter(v => v?.model === 'Kokoro v1.0' || v?.model_name === 'Kokoro v1.0');

            } catch (err) {
                console.error('Failed to load voices:', err);
            }
        }
        async function addVoice() {
            // Open the modal picker instead of creating a dummy voice
            showVoicePicker.value = true;
            await nextTick();
        }

        function closeVoicePicker() {
            showVoicePicker.value = false;
            voiceSearchTerm.value = '';
        }

        function selectVoiceFromPicker(voice) {
            // Determine a key to use in voice_weights
            const baseName = voice.voice_name || voice.name || voice.voice || 'voice';
            let key = baseName;
            let i = 1;
            while (key in localConfig.voice_weights) {
                key = `${baseName}_${i}`;
                i++;
            }
            localConfig.voice_weights[key] = 1;
            emitUpdate();
            nextTick(() => {
                if (voicesList.value) {
                    voicesList.value.scrollTop = voicesList.value.scrollHeight;
                }
            });
            closeVoicePicker();
        }

        watch(
            () => props.config,
            (newVal) => {
                Object.assign(localConfig, {
                    voice_weights: newVal.voice_weights || {},
                    speed: newVal.speed ?? 1.0
                });
            },
            { deep: true }
        );

        return {
            localConfig,
            voicesList,
            allVoices,
            updateWeight,
            removeVoice,
            addVoice,
            emitUpdate,
            showVoicePicker,
            voiceSearchTerm,
            filteredVoices,
            closeVoicePicker,
            selectVoiceFromPicker,
            formatKokoroVoiceName
        };
    }
});
</script>

<style scoped>
.settings-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    min-width: 0;
    flex-shrink: 0;
    flex: 1 1 auto;
    overflow: hidden;
}

.voices-container {
    flex: 1 1 auto;
    min-height: 0;
    box-sizing: border-box;
    height: 10rem;
    overflow-y: auto;
    overflow-x: hidden;
}

.section-label {
    font-weight: 600;
    margin-bottom: 0.5rem;
    display: block;
}

.voice-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border-color);
}

.voice-row--header {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    font-weight: 600;
    border-bottom: 2px solid var(--border-color);
    padding: 0.5rem 0;
}

.header-cell:nth-child(1) {
    padding: 0 1rem;
}

.header-cell:nth-child(2) {
    text-align: center;
}

.voice-card {
    flex: 1;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    width: 100%;
    transition: background 0.2s;
}

.voice-card:hover {
    background: #f7f7f7;
}

.voice-name-display {
    display: block;
    width: 100%;
    font-size: 1rem;
    font-weight: 500;
    color: var(--text-primary);
}

.voice-weight {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    margin: 0;
}

.voice-weight input {
    width: 60px;
    padding: 0.25rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    text-align: center;
}

.btn-delete {
    background: none;
    border: none;
    color: var(--grey);
    cursor: pointer;
    display: flex;
    align-items: center;
    width: 32px;
    height: 32px;
    border-radius: 20%;
}

.btn-add-voice {
    margin-top: auto;
    margin: 0.75rem;
    padding: 8px 16px;
    font-size: 1rem;
    background-color: #F2F4F7;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: background 0.2s;
}

.speed-container {
    margin-top: auto;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-color);
}

.slider-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.25rem;
}

.slider-value {
    font-size: 0.9rem;
    color: var(--text-primary);
}

input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    background: var(--border-color);
    border-radius: 3px;
    outline: none;
}

input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    background: var(--purple);
    border-radius: 50%;
    cursor: pointer;
}

.voice-picker-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.voice-picker-modal {
    background: #fff;
    width: 0px;
    max-height: 80vh;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.voice-picker-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
}

.vp-close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
}

.vp-search-input {
    margin: 0.75rem 1rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    width: calc(100% - 2rem);
    box-sizing: border-box;
}

.vp-list {
    flex: 1 1 auto;
    overflow-y: auto;
}

.vp-item {
    padding: 0.6rem 1rem;
    display: flex;
    justify-content: space-between;
    cursor: pointer;
    border-bottom: 1px solid var(--border-color);
    transition: background 0.15s;
}

.vp-item:hover {
    background: #f5f5f5;
}

.vp-item-name {
    font-weight: 500;
}

.vp-item-model {
    font-size: 0.8rem;
    color: #666;
}

.vp-empty {
    padding: 1rem;
    text-align: center;
    color: #888;
}

.vp-actions {
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--border-color);
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}

.vp-cancel-btn {
    padding: 6px 14px;
    background: #ddd;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}
</style>
